import moment from 'moment';
import Link from 'next/link';
import React from 'react';
import { match } from 'ts-pattern';

import {
  TransactionBought,
  TransactionForSale,
  TransactionMint,
  TransactionTransfer,
  TransactionWithdraw,
} from '@/assets';
import { Images } from '@/components';
import useToUsdWithDenom from '@/hooks/useToUsdWithDenom';
import { currencyFormatter } from '@/utils/currency';

import { ACTIVITY_COLUMNS_ID, ACTIVITY_TRANSACTION_TYPE } from './constants';
import type { ActivityDataTable } from './helpers';

const ActivityCell: React.FC<{
  data: ActivityDataTable;
  columnKey: React.Key;
}> = ({ data, columnKey }) => {
  const { getPriceUsdWithDenom } = useToUsdWithDenom();
  const cellValue = data[columnKey as keyof ActivityDataTable];

  const renderTransactionType = (type: ACTIVITY_TRANSACTION_TYPE) => {
    return match(type)
      .with(ACTIVITY_TRANSACTION_TYPE.Bought, () => (
        <div className="flex items-center">
          <TransactionBought className="w-6" />
          Bought
        </div>
      ))
      .with(ACTIVITY_TRANSACTION_TYPE.ForSale, () => (
        <div className="flex items-center whitespace-nowrap">
          <TransactionForSale className="w-6" />
          For sale
        </div>
      ))
      .with(ACTIVITY_TRANSACTION_TYPE.Transfer, () => (
        <div className="flex items-center">
          <TransactionTransfer className="w-6" />
          Transfer
        </div>
      ))
      .with(ACTIVITY_TRANSACTION_TYPE.Withdraw, () => (
        <div className="flex items-center">
          <TransactionWithdraw className="w-6" />
          Withdraw
        </div>
      ))
      .with(ACTIVITY_TRANSACTION_TYPE.Mint, () => (
        <div className="flex items-center">
          <TransactionMint className="w-6" />
          Mint
        </div>
      ))
      .otherwise(() => (
        <div className="flex items-center">
          <TransactionTransfer className="w-6" />
          {data.transactionType}
        </div>
      ));
  };

  const renderCell = () => {
    return match(columnKey)
      .with(ACTIVITY_COLUMNS_ID.TransactionType, () => renderTransactionType(data.transactionType))
      .with(ACTIVITY_COLUMNS_ID.Item, () => (
        <Link href={`/artwork/${data.assetId}`} className="flex items-center">
          <div className="mr-3 h-[60px] w-[60px] overflow-hidden rounded-[12px] sm:h-[40px] sm:w-[40px] sm:rounded-[6px]">
            <Images src={data.thumbnail} alt="item" />
          </div>
          <p className="line-clamp-2">{data.name}</p>
        </Link>
      ))
      .with(ACTIVITY_COLUMNS_ID.Price, () => (
        <div className="text-right">
          {data.price ? (
            <>
              <p className="font-semibold">
                {currencyFormatter(Number(data.price), { significantDigits: 3 })} {data.denom}
              </p>
              <span className="text-[12px]">
                $
                {currencyFormatter(getPriceUsdWithDenom(Number(data.price), data.denom), {
                  significantDigits: 3,
                })}
              </span>
            </>
          ) : (
            '--'
          )}
        </div>
      ))
      .with(ACTIVITY_COLUMNS_ID.Time, () => <span>{moment(data.time).format('ll')}</span>)
      .otherwise(() => <span>{cellValue}</span>);
  };

  return renderCell();
};

export default ActivityCell;
